import React, { useCallback, useRef, useState } from 'react';

import { Grow, Popper } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import CompetencyProgressSelect from 'components/MeshList/levelUp/competencyScreen/CompetencyProgressSelect';
import { sumBy } from 'lodash';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { incrementRefetchAgendaList } from 'reducers/refetchAgendaListSlice';
import styled from 'styled-components';
import { StyledGrid, VerticallyAlignedFlexContainer } from 'styles/common';

import { PlusEncircled } from 'assets/assetsV1/iconComponents/PlusEncircled';

import { AGENDA_TAG_LABEL_TYPES } from 'constants/ProjectConstants';
import { generateKey, getQueryParams } from 'constants/helper';
import { CheckIcon2 } from 'constants/icons';
import { sendAddedToOneOnOneAgendaEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import theme from 'constants/theme';

import { addCompetencyToAgenda } from 'services/growth/oneOnOne';

import { Typography13, Typography14 } from 'uiToolkit/Typography';
import { Button } from 'uiToolkitV1/Button';

import CompetencyActivityGraph from '../components/CompetencyActivityGraph';
import CompetencyCheckInModal from '../components/CompetencyCheckInCard';
import CompetencyProgressBar from '../components/CompetencyProgressBar';
import { ProgressGridElement } from '../components/common';
import useLevelUpSidePanelContext from '../context';

const Progress = ({ source = '' }) => {
  const {
    state: {
      levelUpCompetencyDetails,
      levelUpTargetDates,
      levelUpProgressStatuses,
      isLoggedInEmployeeManager,
      reporteeId,
    },
    handleCompetencyProgressStatusChange,
    handleCompetencyTargetDateChange,
    forceReload,
  } = useLevelUpSidePanelContext();

  const {
    competencyId,
    jobFunction,
    jobRole,
    progress,
    targetDate,
    progressBar,
    competencyStatus,
    activity,
    accessible,
    levelUpId,
    showAddToAgenda,
  } = levelUpCompetencyDetails;

  const queryClient = useQueryClient();

  const anchorRef = useRef(null);

  const [newProgressValue, setNewProgressValue] = useState(null);

  const [showCheckInModal, setShowCheckInModal] = useState(false);

  const hideCheckInModal = () => setShowCheckInModal(false);

  const handleCompetencyProgressStatusChangeCurried =
    ({ subComptencyId }) =>
    async ({ newProgressValue, comment }) => {
      const res = await handleCompetencyProgressStatusChange({
        jobFunctionId: jobFunction.uuid,
        jobRoleId: jobRole.uuid,
        competencyId,
        progress: newProgressValue,
        subComptencyId,
        comment,
      });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.CompetencySidePanel] });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.LevelUpCompetencyData] });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.LevelUpReporteeData] });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.mySummary.mySummaryCompetency] });
      hideCheckInModal();
    };

  const handleProgressStatusChange = useCallback(({ progress }) => {
    setNewProgressValue(progress);
    setShowCheckInModal(true);
  }, []);

  const levelUpCompetency = {
    levelUpId,
    competencyId,
    subCompetencyId: null,
  };

  const activityCountSum = sumBy(activity, 'progressPercentage');

  const [addedToNextOneOnOne, setAddedToNextOneOnOne] = useState(false);

  const dispatch = useDispatch();

  const history = useHistory();

  const { location } = history;
  const queryString = location.search;

  const { isOneOnOne } = getQueryParams(queryString);

  const [isAddingToOneOnOne, setIsAddingToOneOnOne] = useState(false);

  const addToOneOnOneAgendaHandler = async ({ employeeId, levelUpId, competencyId }) => {
    const labelType = AGENDA_TAG_LABEL_TYPES.LABEL_LEVELUP.value;
    try {
      setIsAddingToOneOnOne(true);
      const res = await addCompetencyToAgenda({
        employeeId,
        levelUpId: Number(levelUpId),
        competencyId,
        labelType,
        uiKey: generateKey(7),
      });
      if (isOneOnOne) {
        dispatch(
          incrementRefetchAgendaList()
          // addToAgendaQueueAction({
          //   labelType,
          //   uuid: res.label.uuid,
          //   isTagged: true,
          //   levelUpInfo: levelUpCompetency
          // })
        );
      }
      setAddedToNextOneOnOne(true);
    } catch (err) {
      console.error('Error adding competency to 1:1 agenda : ', err);
    } finally {
      setIsAddingToOneOnOne(false);
    }
  };

  return (
    <div>
      <VerticallyAlignedFlexContainer style={{ marginBottom: '1rem', marginTop: '1rem' }}>
        <Typography14 weight={600} color={theme.palette.text.blackv1}>
          Progress
        </Typography14>

        {/* <AddToAgendaComponent
          levelUpCompetency={levelUpCompetency}
          type={MESH_LIST_TABS.levelUp.taskType}
          isHiddenByDefault={false}
        /> */}

        {/* commented out as BE support isn't there yet */}
        <VerticallyAlignedFlexContainer style={{ marginLeft: 'auto' }}>
          {addedToNextOneOnOne ? (
            <StyledGrid
              style={{ height: '2.5rem', marginRight: '0.5rem' }}
              container
              alignItems="center"
              justify="center"
            >
              <CheckIcon2
                size="fixed10px"
                containerSize="fixed16px"
                circularOutline
                color={theme.palette.icons.green}
                rightGutter={1}
              />
              <Typography13 color={theme.palette.text.green}>Added to next 1:1 agenda</Typography13>
            </StyledGrid>
          ) : (
            <>
              {showAddToAgenda ? (
                <ButtonColorWrapper>
                  <Button
                    variant="tertiary"
                    StartIcon={PlusEncircled}
                    onClick={() => {
                      sendAddedToOneOnOneAgendaEvent();
                      addToOneOnOneAgendaHandler({
                        employeeId: reporteeId,
                        levelUpId,
                        competencyId,
                      });
                    }}
                    disabled={isAddingToOneOnOne}
                  >
                    Add to next 1:1 agenda
                  </Button>
                </ButtonColorWrapper>
              ) : null}
            </>
          )}
        </VerticallyAlignedFlexContainer>
      </VerticallyAlignedFlexContainer>
      <StyledGrid
        container
        // justify="space-evenly"
        alignItems="center"
        style={{
          paddingLeft: '1px',
          border: `1px solid ${theme.palette.borders.lighterGrey}`,
          borderRadius: '4px',
          padding: '1rem',
        }}
      >
        <ProgressGridElement headingText="Progress">
          <CompetencyProgressSelect
            isDisabled={!accessible}
            levelUpProgressStatuses={levelUpProgressStatuses}
            competencyId={competencyId}
            selectedStatus={progress}
            handleProgressStatusChange={handleProgressStatusChange}
            source={source}
          />
        </ProgressGridElement>

        {/* {accessible && (
          <ProgressGridElement headingText={'Status'}>
            <CompetencyStatus competencyStatus={competencyStatus} />
          </ProgressGridElement>
        )} */}

        <ProgressGridElement style={{ marginLeft: '9rem' }} headingText="Role">
          <Typography13>{jobRole?.name}</Typography13>
        </ProgressGridElement>
      </StyledGrid>
      <div ref={anchorRef} style={{ margin: '1.5rem 0' }}>
        <CompetencyProgressBar
          filledBlocks={progressBar?.filledBlocks}
          totalBlocks={progressBar?.totalBlocks}
          progress={progress}
          barHeight="0.625rem"
          percentage={progressBar?.percentage}
        />
      </div>

      {accessible && activity && Boolean(activityCountSum) && (
        <div style={{ height: '14rem' }}>
          <CompetencyActivityGraph
            levelUpProgressStatuses={levelUpProgressStatuses}
            data={activity}
            competencyId={competencyId}
          />
        </div>
      )}

      {/* <ClickAwayListener onClickAway={hideCheckInModal}> */}
      <Popper
        style={{ zIndex: 5000 }}
        anchorEl={anchorRef.current}
        open={showCheckInModal}
        placement="bottom"
        disablePortal
        modifiers={{
          preventOverflow: {
            enabled: true,
            boundariesElement: 'window',
          },
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <CompetencyCheckInModal
              progress={progress}
              newProgressValue={newProgressValue}
              levelUpProgressStatuses={levelUpProgressStatuses}
              handleCompetencyProgressStatusChange={handleCompetencyProgressStatusChangeCurried({
                subComptencyId: null,
              })}
              hideCheckInModal={hideCheckInModal}
            />
          </Grow>
        )}
      </Popper>
      {/* </ClickAwayListener> */}
    </div>
  );
};

const ButtonColorWrapper = styled('div')`
  color: ${theme.palette.text.blue};
  margin-left: auto;
  p {
    color: ${theme.palette.text.blue} !important;
    font-weight: 400;
  }
  svg {
    color: ${theme.palette.text.blue} !important;
  }
`;

export default Progress;
